import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearTableSession, selectGuestToken, selectTableSession } from './tableSessionSlice';
import { useGetTableSessionQuery } from './tableSessionApi';
import TableLanding from './TableLanding';
import MenuPage from '@/features/menu/MenuPage';

/**
 * Renders the dine-in surface at /:restaurantUuid/table/:tableToken.
 *
 * Until the guest has joined the table session, this shows the join flow
 * (TableLanding, which auto-joins on resolve). Once a session is active for
 * this restaurant it renders the menu — at the SAME table-scoped URL, so the
 * dine-in URL never collapses to the online-ordering /:restaurantUuid path.
 *
 * SC-513: a persisted session whose stored tableToken equals the URL token is
 * NOT proof that the session still belongs to that table. A staff 转台 moves the
 * session to another table while the stored token keeps pointing at the old one,
 * so rescanning the old QR used to short-circuit straight into the moved session
 * — a guest seated at table 1 ended up ordering on table 10. The stored token is
 * therefore only a fast pre-filter; the session's live table (from current/)
 * decides. On a mismatch we drop the local session and fall through to
 * TableLanding, whose join call the backend already resolves correctly:
 * join_table_session scopes the open session to the SCANNED table, so a
 * moved-away table yields a clean new session instead of a rejoin.
 */
export default function TableRoute() {
  const { restaurantUuid = '', tableToken = '' } = useParams<{
    restaurantUuid: string;
    tableToken: string;
  }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectTableSession);
  const guestToken = useAppSelector(selectGuestToken);

  // Cheap local pre-filter: a session for another restaurant or another table
  // token can never be the one behind this URL, and needs no server round-trip.
  const claimsThisToken =
    session !== null &&
    session.restaurantUuid === restaurantUuid &&
    session.tableToken === tableToken;

  // Same args as TablePanel's poll → RTK Query serves both from one cache entry,
  // so this verification costs no extra request beyond the very first one.
  const { data, error } = useGetTableSessionQuery(
    {
      restaurantUuid,
      sessionUuid: session?.sessionUuid ?? '',
      guestToken: guestToken ?? '',
    },
    { skip: !claimsThisToken || !guestToken },
  );

  // The URL's :tableToken is a QR `qrcode` on the guest path and a table uuid on
  // the waiter path (WaiterShell), so either identifier counts as a match. A
  // server that sends neither (pre-SC-513) leaves nothing to check and the
  // session stands.
  //
  // SC-976: a merged tablée's session answers to every member table's
  // identifiers, not just the one it was opened from — current/ sends the
  // full sets (table_tokens/table_uuids); an older server or a single-table
  // session falls back to the lone table_token/table_uuid, unchanged. An
  // EMPTY list also falls back (rather than being treated as "the server
  // sent an authoritative empty set") — a degenerate/buggy response must not
  // be able to switch the SC-513 guard off by shipping `[]`.
  const liveTableIds = data
    ? [
        ...(data.table_tokens && data.table_tokens.length > 0
          ? data.table_tokens
          : [data.table_token]),
        ...(data.table_uuids && data.table_uuids.length > 0 ? data.table_uuids : [data.table_uuid]),
      ].filter(Boolean)
    : [];
  const movedAway = liveTableIds.length > 0 && !liveTableIds.includes(tableToken);

  useEffect(() => {
    if (movedAway && restaurantUuid) dispatch(clearTableSession(restaurantUuid));
  }, [movedAway, restaurantUuid, dispatch]);

  if (!claimsThisToken || movedAway) return <TableLanding />;

  // Verification still in flight. A failed check (offline; a 401 is already
  // handled by TablePanel) must not lock the guest out of a session it cannot
  // disprove, so only the pending state waits.
  if (!data && !error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>{t('tableLanding.loading')}</p>
      </div>
    );
  }

  return <MenuPage />;
}
